import { useEffect, useRef, useState } from "react";
import { useOoDay, type BoardOperator, type DcDuty, type HallTimesMap, type HallStateMap } from "../../hooks/useOoDay";
import { TimePicker } from "../../components/shared";
import type { HallAdminMap } from "./scoring";
import { Diam, Xmark } from "./v8icons";
import css from "./board.module.css";

/** Русские подписи филиалов (19.08, Саша); незнакомый id — как есть капсом. */
import { ruBranch } from "./ruBranch";   // 16.10: подпись филиала — в своём файле (её зовёт и «+» доски)

/** R8.4 Админ зала (19.08, Саша): одна строка чипов-филиалов [20B · Ваня ✕];
 *  тап по чипу — выбор оператора, ✕ — снять. Long-press по панели — личный
 *  фильтр «какие филиалы показывать» (localStorage: админы обычно нужны не
 *  на всех точках, большинству хватает одной-двух). Пустой набор = все.
 *  Long-press по чипу С оператором (25.08, владелец) — TimePicker «от–до»:
 *  часы админства; снятие оператора (✕) чистит и время.
 *  11.10 (владелец: второй админ зала — из дашборда): у филиала бывает двое — по чипу на каждого,
 *  как у DC; тап по чипу — заменить ЭТОГО админа, ✕ — снять только его; часы у филиала общие.
 *  11.10 (владелец: «во вкладке „Расстановка“, в строке, где DC и админы, — назначить второго админа,
 *  удалить старого, назначить нового»): тап по чипу с админом — меню «＋ Второй админ», «✕ Снять»
 *  и ниже «Заменить на…» (список). Правки — черновик доски, как всё остальное до «Сохранить». */
const HA_LS = "ub_ha_branches";

/** «12:00» → «12» в чипе (минуты 00 прячем — экономия ширины). */
const fmtT = (t: string | null) => (t ? (t.endsWith(":00") ? t.slice(0, 2) : t) : "");

export function AdminStrip({ branches, hall, hallTimes, hallState = {}, operators, opById, nameOf = (id) => id, onSet, onSetTime, dcs = [], onAddDc, onRemoveDc, onSetDcTime }: {
  /** 28.09 (владелец: админу зала — «Принять» в Telegram): не разослано — пунктир, принял — свечение, как DC. */
  hallState?: HallStateMap;
  branches: string[];
  /** 02.09 (владелец): дежурный DC — первые чипы строки, зелёные; список — с ролью DC первыми.
   *  27.09 (владелец): «тап на другого должен добавлять человека к DC, а не заменять его;
   *  если надо заменить — проще удалить имеющегося». Дежурных несколько: выбор в списке
   *  ДОБАВЛЯЕТ, ✕ на чипе снимает; неразосланный (до «Отправить») — пунктиром. */
  dcs?: DcDuty[];
  onAddDc?: (operatorId: string) => void;
  onRemoveDc?: (operatorId: string) => void;
  hall: HallAdminMap;
  hallTimes: HallTimesMap;
  operators: BoardOperator[];
  opById: ReturnType<typeof useOoDay>["opById"];
  /** 16.10 (архив 1С В9): подпись админа, которого нет в списке доски (уволен) — board.nameOf; без неё — id, как раньше. */
  nameOf?: (id: string) => string;
  onSet: (branch: string, ids: string[]) => void;
  onSetTime: (branch: string, from: string | null, to: string | null) => void;
  /** 09.09 (владелец): часы дежурства DC — по удержанию чипа, как у админов зала. */
  onSetDcTime?: (operatorId: string, from: string | null, to: string | null) => void;
}) {
  const [popFor, setPopFor] = useState<string | null>(null);
  const [popOp, setPopOp] = useState<string | null>(null);   /* какого админа филиала меняем (null — пустой чип) */
  const [timeFor, setTimeFor] = useState<string | null>(null);
  const [dcTimeFor, setDcTimeFor] = useState<string | null>(null);
  const dcTime = dcs.find((d) => d.id === dcTimeFor) ?? null;
  /* свои ссылки, а не общие с чипами филиалов: иначе флаг удержания от соседнего чипа
     съедал первый тап по DC и список операторов не открывался */
  const lpDc = useRef(0); const lpDcFired = useRef(false); const lpDcStart = useRef<{ x: number; y: number } | null>(null);
  const lpChip = useRef(0);
  const lpFired = useRef(false);
  const lpChipStart = useRef<{ x: number; y: number } | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [visible, setVisible] = useState<string[]>(() => {
    try { const v = JSON.parse(localStorage.getItem(HA_LS) || "null") as unknown; return Array.isArray(v) ? v.map(String) : []; } catch { return []; }
  });
  const lpT = useRef(0);
  const lpStart = useRef<{ x: number; y: number } | null>(null);
  /* фильтр закрывается тапом в ЛЮБОМ месте вне него (19.08, Саша) */
  useEffect(() => {
    if (!filterOpen) return;
    const onDown = (e: PointerEvent) => { const t = e.target as HTMLElement | null; if (!t?.closest?.(`.${css.inlinePop}`)) setFilterOpen(false); };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [filterOpen]);
  if (branches.length === 0) return null;
  const taken = new Set(Object.values(hall).flat());
  const cands = popFor === "__dc"
    ? operators.filter((o) => !dcs.some((d) => d.id === o.id)).sort((a, b) => Number(b.isDc) - Number(a.isDc) || a.name.localeCompare(b.name, "ru"))
    : popFor ? operators.filter((o) => !taken.has(o.id)) : [];
  const shown = visible.length ? branches.filter((b) => visible.includes(b)) : branches;
  const toggleBranch = (br: string) => setVisible((v) => {
    const cur = v.length ? v : branches;
    const next = cur.includes(br) ? cur.filter((x) => x !== br) : [...cur, br];
    const clean = next.length ? next : branches; // пустой набор бессмыслен → все
    try { localStorage.setItem(HA_LS, JSON.stringify(clean)); } catch { /* private mode */ }
    return clean;
  });
  return (
    <div className={css.adminCard}
      onPointerDown={(e) => { lpStart.current = { x: e.clientX, y: e.clientY }; lpT.current = window.setTimeout(() => setFilterOpen(true), 450); }}
      onPointerMove={(e) => { const s = lpStart.current; if (s && (Math.abs(e.clientX - s.x) > 8 || Math.abs(e.clientY - s.y) > 8)) window.clearTimeout(lpT.current); /* свайп строки ≠ long-press */ }}
      onPointerUp={() => window.clearTimeout(lpT.current)} onPointerLeave={() => window.clearTimeout(lpT.current)}>
      <div className={css.adminStrip}>
        <span className={css.adminDiam}><Diam /></span>
        {onAddDc ? (dcs.length ? dcs : [null]).map((dc) => (
          <button key={dc?.id ?? "none"} type="button"
            className={`${css.haChip} ${css.dcChip} ${dc ? css.dcSet : ""} ${dc?.accepted ? css.dcOn : ""} ${dc && !dc.sent ? css.dcDraft : ""}`}
            title={dc
              ? `${!dc.sent ? "Не отправлено — «Ты DC» уйдёт по «Отправить». " : dc.accepted ? "Дежурство принято. " : "Ещё не подтвердил в Telegram. "}Тап — добавить ещё дежурного, удержание — часы, ✕ — снять`
              : "Дежурный DC на день — тап, чтобы назначить"}
            onPointerDown={(e) => {
              e.stopPropagation(); /* панельный long-press (фильтр) с чипа не взводим */
              /* крестик «снять» — не повод открывать часы */
              if (!dc || !onSetDcTime || (e.target as HTMLElement | null)?.closest?.(`.${css.adminX}`)) return;
              lpDcFired.current = false; lpDcStart.current = { x: e.clientX, y: e.clientY };
              lpDc.current = window.setTimeout(() => { lpDcFired.current = true; setDcTimeFor(dc.id); }, 450);
            }}
            onPointerMove={(e) => { const s = lpDcStart.current; if (s && (Math.abs(e.clientX - s.x) > 8 || Math.abs(e.clientY - s.y) > 8)) window.clearTimeout(lpDc.current); }}
            onPointerUp={() => window.clearTimeout(lpDc.current)}
            onPointerLeave={() => window.clearTimeout(lpDc.current)}
            onContextMenu={(e) => e.preventDefault()}
            onClick={() => { if (lpDcFired.current) { lpDcFired.current = false; return; } setPopFor((p) => (p === "__dc" ? null : "__dc")); }}>
            <b>DC</b><span className={css.haNm}>&nbsp;· {dc?.name ?? "—"}</span>
            {dc?.from && dc?.to ? <span className={css.haTime}>{fmtT(dc.from)}–{fmtT(dc.to)}</span> : null}
            {dc && onRemoveDc ? <span className={css.adminX} role="button" aria-label={`Снять ${dc.name} с DC`} onClick={(e) => { e.stopPropagation(); onRemoveDc(dc.id); }}><Xmark /></span> : null}
          </button>
        )) : null}
        {shown.flatMap((br) => { const ids = hall[br] ?? []; return (ids.length ? ids : [null]).map((opId) => {
          const nm = opId ? (opById.get(opId)?.name ?? nameOf(opId)) : null;
          const ht = hallTimes[br];
          const st = opId ? hallState[br]?.[opId] : undefined;
          return (
            <button
              key={`${br}:${opId ?? ""}`} type="button" className={`${css.haChip} ${nm ? css.haOn : ""} ${nm && !st?.sent ? css.haDraft : ""} ${st?.accepted ? css.haAcc : ""}`}
              title={opId ? "Long-press — часы админства" : undefined}
              onClick={() => { if (lpFired.current) { lpFired.current = false; return; } const open = popFor === br && popOp === opId; setPopFor(open ? null : br); setPopOp(opId); }}
              onPointerDown={(e) => {
                e.stopPropagation(); /* панельный long-press (фильтр) не взводим с чипа */
                if (!opId) return;
                lpFired.current = false; lpChipStart.current = { x: e.clientX, y: e.clientY };
                lpChip.current = window.setTimeout(() => { lpFired.current = true; setTimeFor(br); }, 450);
              }}
              /* порог 8px: палец дрожит, любой pointermove убивал long-press (26.08, владелец) */
              onPointerMove={(e) => { const s = lpChipStart.current; if (s && (Math.abs(e.clientX - s.x) > 8 || Math.abs(e.clientY - s.y) > 8)) window.clearTimeout(lpChip.current); }}
              onPointerUp={() => window.clearTimeout(lpChip.current)}
              onPointerLeave={() => window.clearTimeout(lpChip.current)}
              onContextMenu={(e) => e.preventDefault()}
            >
              <b>{ruBranch(br)}</b><span className={css.haNm}>&nbsp;· {nm ?? "—"}</span>
              {ht?.from && ht?.to ? <span className={css.haTime}>{fmtT(ht.from)}–{fmtT(ht.to)}</span> : null}
              {opId ? <span className={css.adminX} role="button" aria-label="Снять" onClick={(e) => { e.stopPropagation(); onSet(br, ids.filter((x) => x !== opId)); }}><Xmark /></span> : null}
            </button>
          );
        }); })}
      </div>
      {popFor ? (
        <div className={`${css.inlinePop} ${css.open}`}>
          {popFor !== "__dc" ? (() => {
            const ids = hall[popFor] ?? []; const cur = popOp && ids.includes(popOp) ? popOp : null;
            const nm = cur ? (opById.get(cur)?.name ?? nameOf(cur)) : "";
            if (!cur) return ids.length ? <div className={css.inlineCap}>Второй админ {ruBranch(popFor)} — кого:</div> : null;
            return (<>
              <div className={css.inlineCap}>{nm} · админ {ruBranch(popFor)}</div>
              {ids.length < 2 ? <div className={`${css.inlineItem} ${css.inlineAct}`} role="button" onClick={() => setPopOp(null)}>＋ Второй админ на {ruBranch(popFor)}</div> : null}
              <div className={`${css.inlineItem} ${css.inlineDel}`} role="button" onClick={() => { onSet(popFor, ids.filter((x) => x !== cur)); setPopFor(null); }}>✕ Снять {nm}</div>
              <div className={css.inlineCap}>Заменить на:</div>
            </>);
          })() : null}
          {cands.length === 0 ? <div className={css.inlineEmpty}>Нет свободных операторов</div>
            : cands.map((o) => <div key={o.id} className={`${css.inlineItem} ${popFor === "__dc" && o.isDc ? css.inlineDc : ""}`}
                onClick={() => { if (popFor === "__dc") onAddDc?.(o.id); else { const ids = hall[popFor] ?? []; onSet(popFor, popOp && ids.includes(popOp) ? ids.map((x) => (x === popOp ? o.id : x)) : [...ids, o.id]); } setPopFor(null); }}>{o.name}{popFor === "__dc" && o.isDc ? <span className={css.inlineTag}>DC</span> : null}</div>)}
        </div>
      ) : null}
      {dcTime && onSetDcTime ? (
        <TimePicker
          open mode="range" step={5} label={`DC ${dcTime.name} — часы дежурства`}
          value={dcTime.from && dcTime.to ? `${dcTime.from}–${dcTime.to}` : undefined}
          onCommit={(v) => {
            const m = v.match(/^(\d{2}:\d{2})[–-](\d{2}:\d{2})$/);
            if (m) onSetDcTime(dcTime.id, m[1]!, m[2]!);
            setDcTimeFor(null);
          }}
          onCancel={() => setDcTimeFor(null)}
        />
      ) : null}
      {timeFor ? (
        <TimePicker
          open mode="range" step={15}
          label={`Админ ${ruBranch(timeFor)} — от и до`}
          value={hallTimes[timeFor]?.from && hallTimes[timeFor]?.to ? `${hallTimes[timeFor]!.from}–${hallTimes[timeFor]!.to}` : undefined}
          onCommit={(v) => {
            const m = v.match(/^(\d{2}:\d{2})[–-](\d{2}:\d{2})$/);
            if (m) onSetTime(timeFor, m[1]!, m[2]!);
            setTimeFor(null);
          }}
          onCancel={() => setTimeFor(null)}
        />
      ) : null}
      {filterOpen ? (
        <div className={`${css.inlinePop} ${css.open}`} onPointerDown={(e) => e.stopPropagation()}>
          <div className={css.inlineEmpty}>Показывать филиалы (личная настройка)</div>
          {branches.map((br) => (
            <div key={br} className={css.inlineItem} onClick={() => toggleBranch(br)}>{shown.includes(br) ? "✓ " : "· "}{ruBranch(br)}</div>
          ))}
          <div className={css.inlineItem} onClick={() => setFilterOpen(false)}><b>Готово</b></div>
        </div>
      ) : null}
    </div>
  );
}
